import type { CrowdexSavedAgent } from '@crowdex/agent-contracts';
import { useCallback, useEffect, useState } from 'react';
import { readAccountAgentProfiles, replaceAccountAgentProfiles } from '@/lib/agent-profiles-client';

const AGENT_PROFILES_CHANGED_EVENT = 'crowdex-agent-profiles-changed';

/** Reads and updates the account's agent profiles (`/api/agent-profiles`). */
export function useAccountAgentProfiles() {
  const [profiles, setProfiles] = useState<CrowdexSavedAgent[]>([]);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setProfiles(await readAccountAgentProfiles());
      setError(null);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    }
  }, []);

  useEffect(() => {
    void refresh();
    const sync = () => void refresh();
    window.addEventListener('focus', sync);
    window.addEventListener(AGENT_PROFILES_CHANGED_EVENT, sync);
    return () => {
      window.removeEventListener('focus', sync);
      window.removeEventListener(AGENT_PROFILES_CHANGED_EVENT, sync);
    };
  }, [refresh]);

  const replace = useCallback(async (next: readonly CrowdexSavedAgent[]) => {
    const saved = await replaceAccountAgentProfiles(next);
    setProfiles(saved);
    setError(null);
    window.dispatchEvent(new Event(AGENT_PROFILES_CHANGED_EVENT));
    return saved;
  }, []);

  return { error, profiles, refresh, replace };
}
